import { APP_DOMAINS } from '~/types';
import { NavigationProps } from '../constants';
import { IconButton } from './IconButton';
import { ReactComponent as SettingsIcon } from '~/assets/svg/settings.svg';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { genLink } from '../utils';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { ChangeTheme } from '~/components/shared/Theme';
import { AgentSelector } from '~/components/shared/AgentSelector';
import { useEffect, useState } from 'react';
import { useSession } from '~/components/contexts/SessionProvider';
import { isAdmin } from '~/lib/admin';
import { ChartBarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '~/components/contexts/SubscriptionProvider';
import { FeatureUpsellModal } from '~/components/shared/FeatureUpsellModal';

const Options = ({ appDomain, routes, setAppDomain }: NavigationProps) => {
  if (appDomain) return null;
  const navigate = useNavigate();
  const { agentId } = useParams();
  const { features } = useSubscription();

  return (
    <div className="flex w-full max-w-md flex-wrap justify-center gap-6">
      {Object.keys(routes)
        .slice(0, -1)
        .map((key) => {
          const route = routes[key as APP_DOMAINS];
          return (
            <div key={key} className="w-20 flex-shrink-0">
              <IconButton
                setAppDomain={() => {
                  const domain = key as APP_DOMAINS;
                  const campaignsEnabled = features?.campaigns === true;
                  const calendarEnabled = features?.calendar === true;
                  const isCampaignsBlocked =
                    domain === APP_DOMAINS.CAMPAIGNS && !campaignsEnabled;
                  const isCalendarBlocked =
                    domain === APP_DOMAINS.SCHEDULE && !calendarEnabled;
                  if (isCampaignsBlocked || isCalendarBlocked) {
                    const upsellId = isCampaignsBlocked
                      ? 'upsell_campaigns'
                      : 'upsell_calendar';
                    const upsell = document.getElementById(
                      upsellId,
                    ) as HTMLDialogElement | null;
                    upsell?.showModal();
                    const dialog = document.getElementById(
                      'mobile_menu',
                    ) as HTMLDialogElement;
                    dialog?.close();
                    return;
                  }

                  setAppDomain(domain);
                  const dialog = document.getElementById(
                    'mobile_menu',
                  ) as HTMLDialogElement;
                  dialog?.close();
                  // redirect to the first subroute
                  const firstSubroute = route.routes?.[0]?.route;
                  navigate(
                    genLink({
                      domain,
                      agentIdParams: agentId!,
                      subRoute: firstSubroute,
                    }),
                  );
                }}
                label={route.label}
                icon={route.icon}
              />
            </div>
          );
        })}
    </div>
  );
};

const SubOptions = ({ routes, appDomain, setAppDomain }: NavigationProps) => {
  const { t } = useTranslation('navigation');
  const { agentId } = useParams();
  if (!appDomain) return null;

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <h2 className="text-center text-lg font-semibold">
        {t(routes[appDomain].label)}
      </h2>
      <div className="flex w-full flex-col items-center gap-3">
        {routes[appDomain].routes?.map((route) => (
          <Link
            key={route.route}
            to={genLink({
              domain: appDomain,
              agentIdParams: agentId!,
              subRoute: route.route,
            })}
            className="text-base-content hover:bg-base-100 hover:text-base-content w-full rounded-lg px-4 py-3 text-center transition-colors"
            onClick={() => {
              setAppDomain(null);
            }}
          >
            {t(route.label)}
          </Link>
        ))}
      </div>
    </div>
  );
};

export const MobileNavigation = ({
  appDomain,
  setAppDomain,
  routes,
}: NavigationProps) => {
  const handleOpenSettings = () => {
    setAppDomain(null);
    const dialog = document.getElementById('mobile_menu') as HTMLDialogElement;
    dialog?.showModal();
  };
  const [hideMobileMenu, setHideMobileMenu] = useState(false);
  const { owner } = useSession();
  const { t } = useTranslation('navigation');

  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith('/create_agent')) {
      setHideMobileMenu(true);
    } else {
      setHideMobileMenu(false);
    }
  }, [location]);

  if (hideMobileMenu) return null;

  return (
    <>
      <div className="border-custom-surface-200 bg-base-100 fixed bottom-0 z-10 flex h-[10vh] w-full items-center justify-center border-t">
        <button
          onClick={handleOpenSettings}
          className="hover:bg-custom-info-100 focus:bg-custom-info-100 flex w-full cursor-pointer flex-col items-center gap-1 rounded-md p-1 transition-all duration-150 hover:scale-110 hover:shadow-sm"
        >
          <SettingsIcon
            className="h-5 w-5 text-current"
            aria-label="mobile_settings"
          />
          <label className="text-[9px]">Menú</label>
        </button>
      </div>

      <dialog id="mobile_menu" className="modal">
        <div className="modal-box bg-base-100 flex max-h-screen flex-col p-0">
          <div className="bg-base-100 sticky top-0 z-10 flex w-full items-center justify-between p-4 shadow-sm">
            <h2 className="text-lg font-semibold">Menú</h2>
            <button
              type="button"
              className="btn btn-sm btn-circle btn-ghost"
              onClick={() => {
                const dialog = document.getElementById(
                  'mobile_menu',
                ) as HTMLDialogElement;
                dialog?.close();
                setAppDomain(null);
              }}
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-1 flex-col overflow-y-auto p-4">
            <div className="flex w-full flex-col gap-6">
              <div className="mt-3 w-full px-1">
                <AgentSelector />
              </div>

              <Options
                appDomain={appDomain}
                routes={routes}
                setAppDomain={setAppDomain}
              />

              <SubOptions
                appDomain={appDomain}
                routes={routes}
                setAppDomain={setAppDomain}
              />

              {/* Admin Analytics Button - only show for admin users */}
              {isAdmin(owner.email) && (
                <div className="flex w-full justify-center">
                  <Link
                    to="/admin/analytics"
                    className="hover:bg-custom-info-100 focus:bg-custom-info-100 flex w-20 cursor-pointer flex-col items-center gap-1 rounded-md p-2 transition-all duration-150 hover:scale-110 hover:shadow-sm"
                    onClick={() => {
                      const dialog = document.getElementById(
                        'mobile_menu',
                      ) as HTMLDialogElement;
                      dialog?.close();
                      setAppDomain(null);
                    }}
                  >
                    <ChartBarIcon className="h-5 w-5 text-current" />
                    <label className="text-center text-[9px]">
                      {t('menu_admin')}
                    </label>
                  </Link>
                </div>
              )}

              {/* Referrals Button - only show for owners with referrals enabled */}
              {owner.allow_referral && (
                <div className="flex w-full justify-center">
                  <Link
                    to="/referrals"
                    className="hover:bg-custom-info-100 focus:bg-custom-info-100 flex w-20 cursor-pointer flex-col items-center gap-1 rounded-md p-2 transition-all duration-150 hover:scale-110 hover:shadow-sm"
                    onClick={() => {
                      const dialog = document.getElementById(
                        'mobile_menu',
                      ) as HTMLDialogElement;
                      dialog?.close();
                      setAppDomain(null);
                    }}
                  >
                    <CurrencyDollarIcon className="h-5 w-5 text-current" />
                    <label className="text-center text-[9px]">
                      {t('menu_referrals')}
                    </label>
                  </Link>
                </div>
              )}

              <div className="flex w-full justify-center">
                <ChangeTheme />
              </div>
            </div>
          </div>
        </div>
      </dialog>
      <FeatureUpsellModal id="upsell_campaigns" feature="campaigns" />
      <FeatureUpsellModal id="upsell_calendar" feature="calendar" />
    </>
  );
};
